export const DEFAULT_WORKSPACE_SYNC_WINDOW_DAYS = 7;
export const DEFAULT_WORKSPACE_EXCLUDE_ALL_DAY = false;
export const DEFAULT_WORKSPACE_NO_DUE_DATE = 'keep_empty' as const;

export const NO_DUE_DATE_MODES = [
  'propose_estimate',
  'keep_empty',
  'create_decide_task',
] as const;

export type NoDueDateMode = typeof NO_DUE_DATE_MODES[number];

export interface WorkspaceSyncRulesDraft {
  windowDays: number;
  excludeAllDay: boolean;
  noDueDate: NoDueDateMode;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function validWindowDays(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isSafeInteger(value)
    && value > 0;
}

export function isNoDueDateMode(value: unknown): value is NoDueDateMode {
  return typeof value === 'string' && (NO_DUE_DATE_MODES as readonly string[]).includes(value);
}

export function normalizeWorkspaceSyncRules(syncRules: unknown): WorkspaceSyncRulesDraft {
  const root = record(syncRules);
  const calendar = record(root.calendar);
  const goalDefaults = record(root.goal_defaults);
  return {
    windowDays: validWindowDays(calendar.window_days) ? calendar.window_days : DEFAULT_WORKSPACE_SYNC_WINDOW_DAYS,
    excludeAllDay: typeof calendar.exclude_all_day === 'boolean'
      ? calendar.exclude_all_day : DEFAULT_WORKSPACE_EXCLUDE_ALL_DAY,
    noDueDate: isNoDueDateMode(goalDefaults.no_due_date)
      ? goalDefaults.no_due_date : DEFAULT_WORKSPACE_NO_DUE_DATE,
  };
}

export function workspaceSyncRulesPatch(draft: WorkspaceSyncRulesDraft): Record<string, unknown> {
  return {
    calendar: {
      window_days: draft.windowDays,
      exclude_all_day: draft.excludeAllDay,
    },
    goal_defaults: {
      no_due_date: draft.noDueDate,
    },
  };
}

export function workspaceSyncRulesEqual(
  left: WorkspaceSyncRulesDraft,
  right: WorkspaceSyncRulesDraft,
): boolean {
  return left.windowDays === right.windowDays
    && left.excludeAllDay === right.excludeAllDay
    && left.noDueDate === right.noDueDate;
}
